import { Trophy, Shield, Award } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";

export default function PublicKnockout() {
  const { data, loading } = usePublicData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { teams, standings, matches } = data;

  const getTeam = (id) => teams.find(t => t.id === id);

  // Get knockout matches by type
  const sf1Leg1 = matches.find(m => m.match_type === 'semifinal1_leg1');
  const sf1Leg2 = matches.find(m => m.match_type === 'semifinal1_leg2');
  const sf2Leg1 = matches.find(m => m.match_type === 'semifinal2_leg1');
  const sf2Leg2 = matches.find(m => m.match_type === 'semifinal2_leg2');
  const final = matches.find(m => m.match_type === 'final');
  const thirdPlace = matches.find(m => m.match_type === 'third_place');
  const pcLeg1 = matches.find(m => m.match_type === 'presidents_cup_leg1');
  const pcLeg2 = matches.find(m => m.match_type === 'presidents_cup_leg2');

  // Calculate aggregate scores
  const calcAggregate = (leg1, leg2, teamAId, teamBId) => {
    if (!leg1 || !leg2) return null;
    if (leg1.status !== 'completed' || leg2.status !== 'completed') return null;

    let teamAGoals = 0;
    let teamBGoals = 0;

    // Leg 1
    if (leg1.home_team_id === teamAId) {
      teamAGoals += leg1.home_score || 0;
      teamBGoals += leg1.away_score || 0;
    } else {
      teamAGoals += leg1.away_score || 0;
      teamBGoals += leg1.home_score || 0;
    }

    // Leg 2
    if (leg2.home_team_id === teamAId) {
      teamAGoals += leg2.home_score || 0;
      teamBGoals += leg2.away_score || 0;
    } else {
      teamAGoals += leg2.away_score || 0;
      teamBGoals += leg2.home_score || 0;
    }

    const winnerId = teamAGoals > teamBGoals ? teamAId : teamBGoals > teamAGoals ? teamBId : null;
    const winner = winnerId ? getTeam(winnerId) : null;

    return {
      teamAGoals,
      teamBGoals,
      winnerId,
      winnerName: winner?.name
    };
  };

  // Get team IDs from standings or matches
  const top4 = standings.slice(0, 4);
  const bottom2 = standings.slice(-2);

  const sf1TeamA = sf1Leg1?.home_team_id || top4[0]?.team_id;
  const sf1TeamB = sf1Leg1?.away_team_id || top4[3]?.team_id;
  const sf2TeamA = sf2Leg1?.home_team_id || top4[1]?.team_id;
  const sf2TeamB = sf2Leg1?.away_team_id || top4[2]?.team_id;
  const pcTeamA = pcLeg1?.home_team_id || bottom2[1]?.team_id;
  const pcTeamB = pcLeg1?.away_team_id || bottom2[0]?.team_id;

  const sf1Agg = calcAggregate(sf1Leg1, sf1Leg2, sf1TeamA, sf1TeamB);
  const sf2Agg = calcAggregate(sf2Leg1, sf2Leg2, sf2TeamA, sf2TeamB);
  const pcAgg = calcAggregate(pcLeg1, pcLeg2, pcTeamA, pcTeamB);

  const LegMatch = ({ match, label }) => {
    if (!match) {
      return (
        <div className="bg-secondary/20 rounded-lg p-3 text-center text-sm text-muted-foreground">
          {label} - TBD
        </div>
      );
    }
    const home = getTeam(match.home_team_id);
    const away = getTeam(match.away_team_id);
    const isCompleted = match.status === 'completed';

    return (
      <div className="bg-background rounded-lg p-3">
        <div className="text-xs text-muted-foreground text-center mb-2">
          {label} • {match.match_date}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            {home?.logo ? (
              <img src={home.logo} alt="" className="w-6 h-6 rounded object-cover" />
            ) : (
              <div className="w-6 h-6 bg-secondary rounded flex items-center justify-center">
                <Shield className="w-3 h-3 text-muted-foreground" />
              </div>
            )}
            <span className="text-sm font-medium truncate">{home?.name || 'TBD'}</span>
          </div>
          <span className="font-bold px-2">
            {isCompleted ? `${match.home_score} - ${match.away_score}` : 'vs'}
          </span>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <span className="text-sm font-medium truncate">{away?.name || 'TBD'}</span>
            {away?.logo ? (
              <img src={away.logo} alt="" className="w-6 h-6 rounded object-cover" />
            ) : (
              <div className="w-6 h-6 bg-secondary rounded flex items-center justify-center">
                <Shield className="w-3 h-3 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TwoLegCard = ({ leg1, leg2, aggregate, title, teamA, teamB, accentColor = "primary" }) => {
    const teamAData = getTeam(teamA);
    const teamBData = getTeam(teamB);

    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className={`px-4 py-2 bg-${accentColor}/10 text-xs uppercase tracking-wider text-center font-bold`}>
          {title}
        </div>
        <div className="p-4 space-y-3">
          {/* Teams Header */}
          <div className="flex items-center justify-between text-sm font-semibold px-2">
            <span>{teamAData?.name || 'TBD'}</span>
            <span className="text-muted-foreground">vs</span>
            <span>{teamBData?.name || 'TBD'}</span>
          </div>

          <LegMatch match={leg1} label="Leg 1" />
          <LegMatch match={leg2} label="Leg 2" />

          {/* Aggregate */}
          {aggregate && (
            <div className={`mt-3 p-3 bg-${accentColor}/10 border border-${accentColor}/30 rounded-lg text-center`}>
              <div className="text-xs text-muted-foreground mb-1">Aggregate</div>
              <div className="font-bold text-lg">
                {aggregate.teamAGoals} - {aggregate.teamBGoals}
              </div>
              {aggregate.winnerName && (
                <div className={`text-sm text-${accentColor} font-medium mt-1`}>
                  {aggregate.winnerName} advances
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Knockout Stage
        </h1>
        <p className="text-muted-foreground mt-1">
          Two-leg semi-finals and President's Philanthropic Cup
        </p>
      </div>

      {/* Qualified Teams */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Semi-Final Qualifiers
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {top4.map((team, i) => {
            const teamData = getTeam(team.team_id);
            return (
              <div key={team.team_id} className="bg-background rounded-xl p-4 text-center border-2 border-primary">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mb-2 font-bold mx-auto ${
                  i === 0 ? "bg-amber-500/20 text-amber-500" : "bg-primary/20 text-primary"
                }`}>
                  {i + 1}
                </span>
                {teamData?.logo ? (
                  <img src={teamData.logo} alt="" className="w-12 h-12 rounded-lg object-cover mx-auto mb-2" />
                ) : (
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Shield className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <p className="font-medium text-sm truncate">{team.team_name}</p>
                <p className="text-xs text-muted-foreground">{team.points} pts</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Championship Bracket */}
      <div className="space-y-6">
        <h2 className="font-heading text-xl font-bold uppercase flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Championship Bracket
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Semi-Finals */}
          <div className="space-y-4">
            <h3 className="font-heading text-center text-sm uppercase text-muted-foreground">
              Semi-Finals (Home & Away)
            </h3>
            <TwoLegCard
              leg1={sf1Leg1}
              leg2={sf1Leg2}
              aggregate={sf1Agg}
              title="SF1: 1st vs 4th"
              teamA={sf1TeamA}
              teamB={sf1TeamB}
            />
            <TwoLegCard
              leg1={sf2Leg1}
              leg2={sf2Leg2}
              aggregate={sf2Agg}
              title="SF2: 2nd vs 3rd"
              teamA={sf2TeamA}
              teamB={sf2TeamB}
            />
          </div>

          {/* Connector */}
          <div className="hidden md:flex items-center justify-center">
            <div className="text-center">
              <Trophy className="w-16 h-16 text-amber-500/50 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground uppercase">Aggregate Winners</p>
              <p className="text-xs text-muted-foreground">Advance to Final</p>
            </div>
          </div>

          {/* Final */}
          <div className="space-y-4">
            <h3 className="font-heading text-center text-sm uppercase text-muted-foreground">
              Grand Final
            </h3>
            {final ? (
              <div className="bg-card border-2 border-amber-500/50 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-amber-500/20 text-center">
                  <Trophy className="w-8 h-8 text-amber-500 mx-auto" />
                  <span className="text-xs uppercase tracking-wider font-bold text-amber-500">Championship Final</span>
                </div>
                <div className="p-4">
                  <LegMatch match={final} label={final.match_date} />
                  {final.status === 'completed' && (
                    <div className="mt-4 p-4 bg-amber-500/20 rounded-lg text-center">
                      <h4 className="font-heading text-lg font-bold text-amber-500">🏆 CHAMPIONS 🏆</h4>
                      <p className="font-bold">
                        {final.home_score > final.away_score
                          ? getTeam(final.home_team_id)?.name
                          : getTeam(final.away_team_id)?.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  Final will be set after semi-finals complete
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3rd Place Match */}
      <div className="space-y-6 pt-6 border-t border-border">
        <h2 className="font-heading text-xl font-bold uppercase flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" />
          3rd Place Match
        </h2>
        <p className="text-muted-foreground text-sm">
          Semi-final losers compete for 3rd place.
        </p>

        {thirdPlace ? (
          <div className="max-w-md">
            <div className="bg-card border border-amber-600/30 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-amber-600/20 text-center">
                <span className="text-xs uppercase tracking-wider font-bold text-amber-600">3rd Place — {thirdPlace.match_date}</span>
              </div>
              <div className="p-4">
                <LegMatch match={thirdPlace} label={thirdPlace.match_date} />
                {thirdPlace.status === 'completed' && (
                  <div className="mt-4 p-3 bg-amber-600/20 rounded-lg text-center">
                    <h4 className="font-heading font-bold text-amber-600">3rd Place</h4>
                    <p className="font-bold">
                      {thirdPlace.home_score > thirdPlace.away_score
                        ? getTeam(thirdPlace.home_team_id)?.name
                        : getTeam(thirdPlace.away_team_id)?.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl p-6 text-center max-w-md">
            <p className="text-muted-foreground text-sm">
              Will be determined after both semi-final legs are complete
            </p>
          </div>
        )}
      </div>

      {/* President's Philanthropic Cup */}
      <div className="space-y-6 pt-6 border-t border-border">
        <h2 className="font-heading text-xl font-bold uppercase flex items-center gap-2">
          <Award className="w-5 h-5 text-purple-500" />
          President's Philanthropic Cup
        </h2>
        <p className="text-muted-foreground text-sm">
          Consolation final for 5th and 6th place teams. Aggregate score over two legs.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* PST Participants */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h4 className="text-sm font-bold uppercase text-muted-foreground mb-3">Participants</h4>
            <div className="grid grid-cols-2 gap-4">
              {bottom2.map((team, i) => {
                const teamData = getTeam(team.team_id);
                return (
                  <div key={team.team_id} className="bg-background rounded-lg p-3 text-center border border-purple-500/30">
                    <span className="text-xs text-muted-foreground">{6 - i}th Place</span>
                    {teamData?.logo ? (
                      <img src={teamData.logo} alt="" className="w-10 h-10 rounded object-cover mx-auto my-2" />
                    ) : (
                      <div className="w-10 h-10 bg-secondary rounded flex items-center justify-center mx-auto my-2">
                        <Shield className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <p className="font-medium text-sm">{team.team_name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PST Matches */}
          <TwoLegCard
            leg1={pcLeg1}
            leg2={pcLeg2}
            aggregate={pcAgg}
            title="President's Cup"
            teamA={pcTeamA}
            teamB={pcTeamB}
            accentColor="purple"
          />
        </div>

        {/* PST Winner */}
        {pcAgg?.winnerName && (
          <div className="p-4 bg-purple-500/20 border border-purple-500/50 rounded-xl text-center">
            <Award className="w-10 h-10 text-purple-500 mx-auto mb-2" />
            <h3 className="font-heading text-xl font-bold text-purple-500">President's Cup Winners!</h3>
            <p className="font-bold text-lg mt-1">{pcAgg.winnerName}</p>
          </div>
        )}
      </div>
    </div>
  );
}
